import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { devLog } from "@/lib/log";
import { toast } from "sonner";
import { Building2, ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

/**
 * `XeroPreviewCard` — Stage-3 STATIC preview only.
 *
 * Renders the "Reconcile with your bank feed" card as a coming-soon
 * illustration. The bank ↔ BuildIntel row is decorative — no MATCH
 * button, no reconcile logic. The visitor's only interaction is the
 * "Email me when bank match ships" waitlist capture, which persists
 * `{user_id, email, feature: "bank_feed", created_at}` server-side and
 * fires a single admin notification via the existing Resend pipeline.
 *
 * DO NOT reintroduce a Match button, connect flow, or OAuth. The
 * feature is a preview promise, not a beta. All lodgement lines in
 * copy must stay "helper workpapers, not lodgement."
 *
 * Idempotency is server-side (same user + feature = same row). On the
 * client, we simply flip the form to a success line after submit. The
 * founder's admin address is NEVER rendered to the tradie — only their
 * own email echoes back in the confirmation.
 */
export default function XeroPreviewCard() {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  // Fetch waitlist state on mount. Pre-fills the input with the logged-in
  // tradie's email so joining is a one-tap flow.
  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const { data } = await api.get("/xero/waitlist");
        if (cancel) return;
        setStatus(data);
        if (data.joined && data.email) setEmail(data.email);
      } catch (err) {
        devLog.warn("bank-feed waitlist status failed", err);
        if (!cancel) setStatus({ joined: false });
      }
    })();
    return () => { cancel = true; };
  }, []);

  useEffect(() => {
    if (!email && user?.email) setEmail(user.email);
  }, [user, email]);

  const join = async (e) => {
    e?.preventDefault?.();
    const clean = email.trim().toLowerCase();
    if (!clean.includes("@") || !clean.split("@")[1]?.includes(".")) {
      toast.error("Enter a valid email");
      return;
    }
    setBusy(true);
    try {
      // Feature key is fixed — this card is exclusively the bank-feed
      // preview. Backend keeps `feature` as its idempotency axis.
      await api.post("/xero/waitlist", { email: clean, feature: "bank_feed" });
      setStatus({ joined: true, email: clean });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save — try again");
    } finally {
      setBusy(false);
    }
  };

  if (!status) return null;

  return (
    <div
      data-testid="xero-preview-card"
      className="relative overflow-hidden rounded-2xl bi-elevate p-4 sm:p-5 bi-rise bi-rise-3"
    >
      <div
        aria-hidden
        className="absolute -top-16 -left-16 w-56 h-56 rounded-full bg-[#60A5FA]/12 blur-3xl pointer-events-none"
      />
      <div className="relative">
        <div className="flex items-center gap-2.5 mb-3">
          <div className="w-10 h-10 rounded-xl bg-white/[0.05] border border-white/10 flex items-center justify-center text-[#60A5FA]">
            <Building2 className="w-5 h-5" strokeWidth={2.4} />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#60A5FA] font-heading font-bold">
              Coming in Stage 3
            </div>
            <div className="font-heading text-lg font-extrabold leading-tight">
              Reconcile with your bank feed
            </div>
          </div>
        </div>

        <p className="text-xs text-white/60 leading-relaxed mb-3">
          Bank matching with Xero/MYOB is planned. Not available yet. Exports stay helper workpapers, not lodgement.
        </p>

        {/* Static illustration — no interaction. Kept side-by-side so
            visitors can picture the pairing but the card promises nothing. */}
        <div
          data-testid="xero-preview-row"
          aria-hidden="true"
          className="rounded-xl border border-white/10 bg-white/[0.02] p-3 flex items-stretch gap-3"
        >
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-wider text-[#60A5FA] mb-0.5">Bank feed</div>
            <div className="text-sm font-medium text-white truncate">Bunnings Alexandria</div>
            <div className="text-[11px] text-white/45">21 Feb · Card ***4821</div>
            <div className="mt-1 font-heading text-base font-extrabold text-white tabular-nums">
              -$189.50
            </div>
          </div>
          <div className="self-center text-white/30">
            <ArrowRight className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-wider text-[#FF9500] mb-0.5">BuildIntel</div>
            <div className="text-sm font-medium text-white truncate">Bunnings — Drill bits</div>
            <div className="text-[11px] text-white/45">21 Feb · Job: Smith Reno</div>
            <div className="mt-1 font-heading text-base font-extrabold text-white tabular-nums">
              $189.50 <span className="text-white/40 text-[10px] font-normal">inc. $17.23 GST</span>
            </div>
          </div>
        </div>

        {/* Waitlist capture — one control only. No platform pills, no
            hardcoded admin address, no green celebratory chip. */}
        {status.joined ? (
          <div
            data-testid="xero-waitlist-joined"
            className="mt-4 text-xs text-white/70 leading-relaxed"
          >
            You&apos;re on the list — we&apos;ll email you when it&apos;s ready.
          </div>
        ) : (
          <form onSubmit={join} className="mt-4" data-testid="xero-waitlist-form">
            <label
              htmlFor="bank-feed-waitlist-email"
              className="block text-[11px] text-white/60 mb-1.5 font-medium"
            >
              Email me when bank match ships.
            </label>
            <div className="flex gap-2">
              <input
                id="bank-feed-waitlist-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@yourtrade.com"
                autoComplete="email"
                inputMode="email"
                autoCapitalize="none"
                spellCheck={false}
                data-testid="xero-waitlist-email"
                className="flex-1 h-11 rounded-lg bg-white/[0.05] border border-white/10 px-3 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#60A5FA]/40"
              />
              <button
                type="submit"
                disabled={busy}
                data-testid="xero-waitlist-submit"
                className="h-11 px-4 rounded-lg bg-[#60A5FA] text-[#0B1220] text-xs font-heading font-bold uppercase tracking-wider inline-flex items-center justify-center gap-1.5 disabled:opacity-60"
              >
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><CheckCircle2 className="w-3.5 h-3.5" /> Notify me</>}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
